import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useMemo } from 'react'
import { db, type LocalChannel } from '../db/db'
import { ensureUsers } from '../sync/ensureUsers'

const NONE: string[] = []

/**
 * Everyone in a conversation, in the order they joined where known. Direct, group and bot conversations list their members in the summary;
 * a channel does not (a company-wide one would be thousands of ids on every sync), so its members come from the members table, which its
 * snapshot fills and member events keep current. Members this device has no name for yet are fetched.
 */
export function useMemberIds(channel: Pick<LocalChannel, 'id' | 'type' | 'memberIds'>): string[] {
  const isChannel = channel.type === 'channel'
  const rows = useLiveQuery(
    async () => (isChannel ? (await db.members.where('channelId').equals(channel.id).toArray()).map((m) => m.userId) : null),
    [channel.id, isChannel],
    null,
  )
  const ids = useMemo(() => {
    if (!isChannel) return channel.memberIds
    // Rows cached before channels stopped listing their members keep their ids until the next summary replaces them.
    if (!rows) return channel.memberIds.length ? channel.memberIds : NONE
    return [...new Set([...channel.memberIds, ...rows])]
  }, [isChannel, channel.memberIds, rows])
  useEffect(() => {
    void ensureUsers(ids)
  }, [ids])
  return ids
}
